import { Layout } from "@/components/Layout/Layout";
import { Eye, UserX, UserCheck, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { MainHeading } from "@/components/common/MainHeading";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { ICustomer, ICustomerListResponse } from "@/interfaces/Customer/customer.interface";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { IFilter } from "@/interfaces/common.interface";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDateTime, GetFilterUrl, showToastNotification, downloadExcel } from "@/utility/helper";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RoutesLink } from "@/utility/RouteLinks";
import { TableFilters } from "@/components/common/TableFilters";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";

export const Customers = () => {
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const { canView, canBlock, canExport } = usePermissions();
    const [customers, setCustomers] = useState<ICustomer[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);

    const [blockTarget, setBlockTarget] = useState<ICustomer | null>(null);
    const [blockLoading, setBlockLoading] = useState(false);

    const { filters, debouncedFilters, updateFilter, handlePageChange, pageInfo, setPageInfo, handlePageSizeChange } = useFilter<IFilter>({
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
    });

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            search: filters.search,
            startDate: filters.startDate,
            endDate: filters.endDate,
        });
        const url = `${API_URL}${API_ENDPOINT.user}/export/excel?${queryParams}&type=customer`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'customers_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<ICustomerListResponse>(
                `${API_URL}${API_ENDPOINT.user}/list?${queryParams}&type=customer`,
                { method: 'GET' }
            );
            setCustomers(response?.data?.users || []);
            setPageInfo(
                response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 },
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.startDate, debouncedFilters.endDate]);

    const handleBlockToggle = async () => {
        if (!blockTarget) return;
        setBlockLoading(true);
        const newBlockedState = !blockTarget.isBlocked;
        try {
            await callApi(
                `${API_URL}${API_ENDPOINT.user}/block/${blockTarget.id}?isBlocked=${newBlockedState}`,
                { method: 'PATCH' }
            );
            showToastNotification(
                'success',
                `Customer has been ${newBlockedState ? 'blocked' : 'unblocked'} successfully.`
            );
            setCustomers(prev =>
                prev.map(c => c.id === blockTarget.id ? { ...c, isBlocked: newBlockedState } : c)
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setBlockLoading(false);
            setBlockTarget(null);
        }
    };

    const columns: TableColumn<ICustomer>[] = [
        {
            key: "fullName",
            header: "Customer",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                        {item.fullName?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div>
                        <p className="font-semibold text-foreground text-sm">{item.fullName}</p>
                        <p className="text-xs text-muted-foreground">{item.email}</p>
                    </div>
                </div>
            ),
        },
        {
            key: "phone",
            header: "Phone",
            render: (item) => <div className="text-muted-foreground text-sm">{item.phone || '—'}</div>,
        },
        {
            key: "createdAt",
            header: "Joined",
            render: (item) => (
                <div className="text-muted-foreground text-xs">{formatDateTime(item.createdAt)}</div>
            ),
        },
        {
            key: "isBlocked",
            header: "Status",
            render: (item) => <StatusBadge status={item.isBlocked ? "blocked" : "active"} />,
        },
    ];

    const getActions = (record: ICustomer): Action<ICustomer>[] => [
        ...(canView(RESOURCES.CUSTOMER) ? [{
            text: 'View Details',
            icon: <Eye strokeWidth={1.5} size={16} className="cursor-pointer" />,
            onClick: () => {
                navigate(prefix + RoutesLink.CustomerDetails.replace(':id', record.id));
            },
        }] : []),
        ...(canBlock(RESOURCES.CUSTOMER) ? [{
            text: record.isBlocked ? 'Unblock' : 'Block',
            icon: record.isBlocked ?
                <UserCheck strokeWidth={1.5} size={16} className="cursor-pointer text-green-500" /> :
                <UserX strokeWidth={1.5} size={16} className="cursor-pointer text-destructive" />,
            onClick: () => {
                setBlockTarget(record);
            },
        }] : []),
    ];

    const blockedCount = customers.filter(c => c.isBlocked).length;
    const activeCount = customers.filter(c => !c.isBlocked).length;

    return (
        <Layout>
            <MainHeading
                title="Customers"
                description={`${pageInfo.total} registered · ${activeCount} active · ${blockedCount} blocked`}
            />
            <div className="mt-6 flex flex-col gap-4">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search customers by name or email..."
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.CUSTOMER) && (
                            <Button
                                variant="outline"
                                onClick={handleExport}
                                className="h-10 px-4 flex items-center gap-2 rounded-xl"
                                leftIcon={<Download className="w-4 h-4" />}
                                isLoading={loadingExport}
                            >
                                Export
                            </Button>
                        )}
                    </div>
                </TableFilters>
                <CommonTable
                    columns={columns}
                    data={customers}
                    loading={loading}
                    emptyMessage="No customers found."
                    actions={getActions}
                />
                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {/* Block / Unblock Confirmation Modal */}
            <ConfirmationModal
                open={!!blockTarget}
                onOpenChange={(open) => !open && setBlockTarget(null)}
                onConfirm={handleBlockToggle}
                loading={blockLoading}
                type={blockTarget?.isBlocked ? "success" : "danger"}
                title={blockTarget?.isBlocked ? "Unblock Customer?" : "Block Customer?"}
                message={
                    blockTarget?.isBlocked
                        ? `Are you sure you want to unblock ${blockTarget?.fullName}? They will regain full access to the platform.`
                        : `Are you sure you want to block ${blockTarget?.fullName}? They will lose access to the platform immediately.`
                }
                confirmText={blockTarget?.isBlocked ? "Yes, Unblock" : "Yes, Block"}
                cancelText="Cancel"
                size="lg"
            />
        </Layout>
    );
};
